import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useMemo } from 'react'

export interface Props {
  title?: string
  description?: string
  image?: string
  canonicalUrl?: string
  children?: React.ReactNode
  index?: boolean
}

export const PageMeta = ({ title, image, description, children, canonicalUrl, index = true }: Props) => {
  const metaTitle = title ? `${title} | Thingtesting` : 'Discover brands, share your honest reviews | Thingtesting'
  const metaDescription =
    description ||
    'Honest product reviews from real people. Read and share your candid reviews of the best home, beauty, food, and health and wellness brands on the internet.'
  const metaImage = image || 'https://cdn.thingtesting.com/assets/meta-2023-06.jpg'
  const { asPath } = useRouter()
  const url = `https://thingtesting.com${asPath}`

  const canonical = useMemo(() => {
    if (canonicalUrl) {
      return canonicalUrl
    } else {
      try {
        const parsedUrl = new URL(url)
        return `${parsedUrl.origin}${parsedUrl.pathname}`
      } catch {
        return url
      }
    }
  }, [url, canonicalUrl])

  return (
    <Head>
      <title>{metaTitle}</title>
      <meta property="og:title" content={metaTitle} key="og:title" />
      <meta property="og:image" content={metaImage} key="og:image" />
      <meta property="og:description" content={metaDescription} key="og:description" />
      <meta property="og:type" content="website" key="og:type" />
      <meta property="og:url" content={url} key="og:url" />
      <meta name="description" content={metaDescription} key="description" />
      <meta name="robots" content="max-image-preview:large" />
      <link rel="canonical" href={canonical} key="canonical" />
      <link rel="alternate" type="application/rss+xml" title="Thingtesting" href="/api/rss.xml" />
      {index === false && <meta name="robots" content="noindex" key="robots:noindex" />}
      {children}
    </Head>
  )
}
